import { Link } from "react-router-dom";
import { Instagram, Mail, MapPin, Phone, Clock } from "lucide-react";
import { Logo } from "@/components/brand/Logo";
import { useSite } from "@/context/SiteContext";
import { igHref, telHref } from "@/lib/contact";
import { inr } from "@/lib/format";
import { NAV_LINKS, POLICY_LINKS, MEDICAL_DISCLAIMER } from "@/data/copy";
import { HoursList } from "@/components/common/HoursList";

const Col = ({ title, children, className = "", testid }) => (
  <div className={`min-w-0 ${className}`} data-testid={testid}>
    <h2 className="mb-4 text-sm font-semibold uppercase tracking-[0.16em] text-white">{title}</h2>
    {children}
  </div>
);

const Row = ({ icon: Icon, children }) => (
  <li className="flex items-start gap-3">
    <Icon className="mt-1 h-4 w-4 shrink-0 text-[#9FD27A]" aria-hidden="true" />
    <span className="min-w-0">{children}</span>
  </li>
);

export const Footer = ({ className = "" }) => {
  const { settings, content } = useSite();
  const year = new Date().getFullYear();
  return (
    <footer className={`relative overflow-hidden bg-brand-navy text-slate-200 ${className}`} data-testid="site-footer">
      <div className="h-1.5 bg-gradient-to-r from-brand-sky via-brand-green to-brand-coral" aria-hidden="true" />
      <span className="blob -right-24 top-10 h-72 w-72 bg-brand-blueBright/20" aria-hidden="true" />
      <span className="blob -left-24 bottom-0 h-64 w-64 bg-brand-green/15" aria-hidden="true" />
      <div className="container-x relative grid grid-cols-2 gap-x-8 gap-y-12 py-16 xl:grid-cols-4 xl:gap-x-10">
        <div className="col-span-2 md:col-span-1" data-testid="footer-brand-col">
          <Link to="/" className="inline-block rounded-2xl bg-white p-3" aria-label="Young Spine Tricity – home">
            <Logo full className="h-28 w-28" />
          </Link>
          <p className="mt-5 font-heading text-lg font-semibold text-white">Precision Spine Care for a Pain-Free Life</p>
          <p className="mt-1 text-sm text-slate-300">Spine Health | Pain Relief | Better Tomorrow</p>
          <a href={igHref(settings)} target="_blank" rel="noopener noreferrer" data-testid="footer-instagram-link" className="mt-5 inline-flex items-center gap-2 text-base text-white hover:text-[#9FD27A]">
            <Instagram className="h-5 w-5" aria-hidden="true" /> @{settings.instagram_handle}
          </a>
        </div>
        <Col title="Visit & contact" className="col-span-2 md:col-span-1" testid="footer-contact-col">
          <ul className="space-y-3 text-base">
            <Row icon={MapPin}>{settings.address}</Row>
            <Row icon={Phone}><a href={telHref(settings)} className="hover:text-white" data-testid="footer-phone-link">{settings.phone_display}</a></Row>
            <Row icon={Mail}><a href={`mailto:${settings.email}`} className="break-all hover:text-white" data-testid="footer-email-link">{settings.email}</a></Row>
            <Row icon={Clock}><HoursList compact /><span className="text-sm text-slate-300">Consultation {inr(settings.consultation_fee)} per visit</span></Row>
          </ul>
        </Col>
        <Col title="Quick links" testid="footer-links-col">
          <ul className="space-y-2.5 text-base">
            {NAV_LINKS.map((l) => <li key={l.to}><Link to={l.to} className="hover:text-white">{l.label}</Link></li>)}
            <li><Link to="/faq" className="hover:text-white">FAQs</Link></li>
          </ul>
        </Col>
        <Col title="Policies" testid="footer-policies-col">
          <ul className="space-y-2.5 text-base">
            {POLICY_LINKS.map((p) => <li key={p.slug}><Link to={`/${p.slug}`} className="hover:text-white" data-testid={`footer-policy-${p.slug}`}>{p.label}</Link></li>)}
          </ul>
        </Col>
      </div>
      <div className="border-t border-white/10">
        <div className="container-x space-y-2 py-6 text-sm text-slate-300">
          <p data-testid="footer-disclaimer">{MEDICAL_DISCLAIMER}</p>
          <p>
            © {year} Amar Healthcare Solutions · {content.doctor_name}, {content.doctor_registration}
            {settings.gst_number ? ` · GSTIN ${settings.gst_number}` : ""}
          </p>
        </div>
      </div>
    </footer>
  );
};
